import React, { useRef } from 'react';
import { ArrowLeft, Download, Film } from 'lucide-react';
import { GlassCard, GlassButton } from '../ui';
import { VideoProduction } from '../../services/api';
import './VideoProduction.css';

interface Props {
  production: VideoProduction;
  onBack: () => void;
}

const ProductionPlayer: React.FC<Props> = ({ production, onBack }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleDownload = () => {
    if (!production.final_url) return;
    const a = document.createElement('a');
    a.href = production.final_url;
    a.download = `${production.title.replace(/[^a-z0-9]/gi, '_')}.mp4`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const totalDuration = production.scenes
    ? production.scenes.reduce((acc, s) => acc + s.duration, 0)
    : null;

  return (
    <div className="vpp-root">
      <div className="vpp-header">
        <GlassButton variant="ghost" onClick={onBack}>
          <ArrowLeft size={16} /> Back
        </GlassButton>
        <h2 className="vpp-title">{production.title}</h2>
        <GlassButton variant="secondary" onClick={handleDownload} disabled={!production.final_url}>
          <Download size={16} /> Download
        </GlassButton>
      </div>

      <GlassCard className="vpp-player-card">
        {production.final_url ? (
          <video
            ref={videoRef}
            className="vpp-video"
            src={production.final_url}
            controls
            playsInline
          />
        ) : (
          <div className="vpp-no-video">
            <Film size={48} />
            <p>Video not available</p>
          </div>
        )}
      </GlassCard>

      <GlassCard className="vpp-meta-card">
        <div className="vpd-meta-grid">
          <div className="vpd-meta-item">
            <span className="vpd-meta-label">Scenes</span>
            <span className="vpd-meta-value">{production.total_scenes}</span>
          </div>
          <div className="vpd-meta-item">
            <span className="vpd-meta-label">Aspect Ratio</span>
            <span className="vpd-meta-value">{production.aspect_ratio}</span>
          </div>
          {totalDuration && (
            <div className="vpd-meta-item">
              <span className="vpd-meta-label">Duration</span>
              <span className="vpd-meta-value">~{Math.round(totalDuration)}s</span>
            </div>
          )}
          {production.completed_at && (
            <div className="vpd-meta-item">
              <span className="vpd-meta-label">Rendered</span>
              <span className="vpd-meta-value">
                {new Date(production.completed_at).toLocaleDateString(undefined, {
                  day: '2-digit', month: 'short', year: 'numeric'
                })}
              </span>
            </div>
          )}
        </div>
      </GlassCard>
    </div>
  );
};

export default ProductionPlayer;
